import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import {
  useAttributes,
  useBrowseProducts,
  useCategoryTree,
  useCreatePublicationCatalogRule,
  useUpdatePublicationCatalogRule,
} from '../../hooks';
import { flattenCategoryTree } from '../../lib/categoryTree';
import {
  buildAttributeValueCriteria,
  buildManualProductCriteria,
  buildMasterCategoryCriteria,
  parseCompositeFilterJson,
  readAttributeValueCriteria,
  readManualProductCriteria,
  readMasterCategoryCriteria,
} from '../../lib/publicationRuleCriteria';
import type { CriteriaDescribeContext } from '../../lib/publicationRuleCriteria';
import type { ToastSeverity } from '../../lib/useToast';
import {
  PUBLICATION_CRITERIA_KIND_OPTIONS,
  PUBLICATION_RULE_DIRECTION_OPTIONS,
} from '../../model/options';
import type { ProductSummary } from '../../model/types';
import type {
  PublicationCatalogRule,
  PublicationCriteriaKind,
  PublicationRuleDirection,
} from '../../model/publicationTypes';
import { PublicationRuleAttributeCriteria } from './PublicationRuleAttributeCriteria';
import { PublicationRuleCategoryCriteria } from './PublicationRuleCategoryCriteria';
import { PublicationRuleCompositeCriteria } from './PublicationRuleCompositeCriteria';
import { PublicationRuleProductCriteria } from './PublicationRuleProductCriteria';

interface PublicationRuleFormDialogProps {
  open: boolean;
  catalogId: string;
  /** The rule being edited, or `null` to create. */
  rule: PublicationCatalogRule | null;
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

/**
 * Add/edit a publication catalog rule. Renders one of four criteria sub-forms depending on the
 * selected `criteriaKind` (MASTER_CATEGORY/ATTRIBUTE_VALUE/MANUAL_PRODUCT get a structured picker;
 * COMPOSITE_FILTER gets a structured summary + raw JSON editor — see
 * `PublicationRuleCompositeCriteria`'s doc comment for why no visual tree builder was built).
 */
export function PublicationRuleFormDialog({
  open,
  catalogId,
  rule,
  onClose,
  onResult,
}: PublicationRuleFormDialogProps) {
  const { t, i18n } = useTranslation('catalog');
  const isEdit = rule !== null;
  const create = useCreatePublicationCatalogRule();
  const update = useUpdatePublicationCatalogRule();
  const pending = create.isPending || update.isPending;

  const categoryTreeQuery = useCategoryTree();
  const attributesQuery = useAttributes();

  const [direction, setDirection] = useState<PublicationRuleDirection>('INCLUDE');
  const [criteriaKind, setCriteriaKind] = useState<PublicationCriteriaKind>('MASTER_CATEGORY');
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [attributeCode, setAttributeCode] = useState('');
  const [attributeValues, setAttributeValues] = useState<string[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<ProductSummary[]>([]);
  const [compositeRawJson, setCompositeRawJson] = useState('');

  // MANUAL_PRODUCT edit: the rule only carries `productIds`, so hydrate the picker's initial
  // selection (name/code for chip display) via a best-effort by-ids browse lookup.
  const editProductIds = isEdit && rule.criteriaKind === 'MANUAL_PRODUCT' ? readManualProductCriteria(rule.criteriaPayload) : [];
  const hydrateProductsQuery = useBrowseProducts(
    { ids: editProductIds, size: editProductIds.length || 1 },
    { enabled: open && editProductIds.length > 0 },
  );

  useEffect(() => {
    if (!open) return;
    if (!rule) {
      setDirection('INCLUDE');
      setCriteriaKind('MASTER_CATEGORY');
      setCategoryIds([]);
      setAttributeCode('');
      setAttributeValues([]);
      setSelectedProducts([]);
      setCompositeRawJson('');
      return;
    }
    setDirection(rule.direction);
    setCriteriaKind(rule.criteriaKind);
    setCategoryIds(
      rule.criteriaKind === 'MASTER_CATEGORY' ? readMasterCategoryCriteria(rule.criteriaPayload) : [],
    );
    if (rule.criteriaKind === 'ATTRIBUTE_VALUE') {
      const { attributeCode: code, values } = readAttributeValueCriteria(rule.criteriaPayload);
      setAttributeCode(code);
      setAttributeValues(values);
    } else {
      setAttributeCode('');
      setAttributeValues([]);
    }
    setCompositeRawJson(
      rule.criteriaKind === 'COMPOSITE_FILTER' ? JSON.stringify(rule.criteriaPayload, null, 2) : '',
    );
    // selectedProducts is populated by the hydrate-effect below once the by-ids lookup resolves.
    setSelectedProducts([]);
  }, [open, rule]);

  useEffect(() => {
    if (hydrateProductsQuery.data) setSelectedProducts(hydrateProductsQuery.data.content);
  }, [hydrateProductsQuery.data]);

  const describeCtx: CriteriaDescribeContext = useMemo(
    () => ({
      categoryNameById: new Map(flattenCategoryTree(categoryTreeQuery.data ?? []).map((n) => [n.id, n.name])),
      attributeDictionary: attributesQuery.data ?? [],
      locale: i18n.language,
    }),
    [categoryTreeQuery.data, attributesQuery.data, i18n.language],
  );

  const compositeParseResult = criteriaKind === 'COMPOSITE_FILTER' ? parseCompositeFilterJson(compositeRawJson) : null;

  const valid = (() => {
    switch (criteriaKind) {
      case 'MASTER_CATEGORY':
        return categoryIds.length > 0;
      case 'ATTRIBUTE_VALUE':
        return attributeCode !== '' && attributeValues.length > 0;
      case 'MANUAL_PRODUCT':
        return selectedProducts.length > 0;
      case 'COMPOSITE_FILTER':
        return compositeRawJson.trim() !== '' && compositeParseResult?.ok === true;
      default:
        return false;
    }
  })();

  const submit = () => {
    if (!valid) return;
    let criteriaPayload;
    switch (criteriaKind) {
      case 'MASTER_CATEGORY':
        criteriaPayload = buildMasterCategoryCriteria(categoryIds);
        break;
      case 'ATTRIBUTE_VALUE':
        criteriaPayload = buildAttributeValueCriteria(attributeCode, attributeValues);
        break;
      case 'MANUAL_PRODUCT':
        criteriaPayload = buildManualProductCriteria(selectedProducts.map((p) => p.id));
        break;
      case 'COMPOSITE_FILTER':
        if (!compositeParseResult?.ok) return;
        criteriaPayload = compositeParseResult.value;
        break;
      default:
        return;
    }
    const handlers = {
      onSuccess: () => {
        onResult('success', isEdit ? t('publicationRulesPanel.updated') : t('publicationRulesPanel.created'));
        onClose();
      },
      onError: (error: { message: string }) => onResult('error', error.message),
    };
    if (isEdit && rule) {
      update.mutate({ catalogId, ruleId: rule.id, body: { direction, criteriaKind, criteriaPayload } }, handlers);
      return;
    }
    create.mutate({ catalogId, body: { direction, criteriaKind, criteriaPayload } }, handlers);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEdit ? t('publicationRulesPanel.formDialog.editTitle') : t('publicationRulesPanel.formDialog.newTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Alert severity="info" variant="outlined">
            {t('publicationRulesPanel.excludeWinsHint')}
          </Alert>
          <TextField
            select
            label={t('publicationRulesPanel.formDialog.directionLabel')}
            value={direction}
            onChange={(e) => setDirection(e.target.value as PublicationRuleDirection)}
            fullWidth
          >
            {PUBLICATION_RULE_DIRECTION_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {t(o.label)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('publicationRulesPanel.formDialog.criteriaKindLabel')}
            value={criteriaKind}
            onChange={(e) => setCriteriaKind(e.target.value as PublicationCriteriaKind)}
            disabled={isEdit}
            helperText={isEdit ? t('publicationRulesPanel.formDialog.criteriaKindImmutableHint') : undefined}
            fullWidth
          >
            {PUBLICATION_CRITERIA_KIND_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {t(o.label)}
              </MenuItem>
            ))}
          </TextField>

          {criteriaKind === 'MASTER_CATEGORY' ? (
            <PublicationRuleCategoryCriteria value={categoryIds} onChange={setCategoryIds} disabled={pending} />
          ) : criteriaKind === 'ATTRIBUTE_VALUE' ? (
            <PublicationRuleAttributeCriteria
              attributeCode={attributeCode}
              values={attributeValues}
              onChangeAttributeCode={setAttributeCode}
              onChangeValues={setAttributeValues}
              disabled={pending}
            />
          ) : criteriaKind === 'MANUAL_PRODUCT' ? (
            <PublicationRuleProductCriteria
              value={selectedProducts}
              onChange={setSelectedProducts}
              disabled={pending}
            />
          ) : (
            <PublicationRuleCompositeCriteria
              rawJson={compositeRawJson}
              onChangeRawJson={setCompositeRawJson}
              parsed={compositeParseResult?.ok ? compositeParseResult.value : null}
              parseError={compositeParseResult && !compositeParseResult.ok ? compositeParseResult.error : null}
              describeCtx={describeCtx}
              disabled={pending}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!valid || pending}
          startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('common:action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
